import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { ArrowLeft, ArrowRight } from "lucide-react";

import { AdSlot } from "@/components/AdSlot";
import { SiteLayout } from "@/components/SiteLayout";
import { getGuide } from "@/lib/guides-content";

export const Route = createFileRoute("/guias/$slug")({
  loader: ({ params }) => {
    const guide = getGuide(params.slug);
    if (!guide) throw notFound();
    return { guide };
  },
  head: ({ params, loaderData }) => {
    const url = `https://www.trajetofacil.com.br/guias/${params.slug}`;
    if (!loaderData) {
      return {
        meta: [{ title: "Guia não encontrado — TrajetoFácil" }, { name: "robots", content: "noindex" }],
      };
    }
    const { guide } = loaderData;
    return {
      meta: [
        { title: `${guide.title} — TrajetoFácil` },
        { name: "description", content: guide.description },
        { property: "og:title", content: guide.title },
        { property: "og:description", content: guide.description },
        { property: "og:type", content: "article" },
        { property: "og:url", content: url },
        { name: "twitter:card", content: "summary" },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: guide.title,
            description: guide.description,
            datePublished: guide.publishedAt,
            dateModified: guide.updatedAt,
            inLanguage: "pt-BR",
            mainEntityOfPage: url,
            publisher: {
              "@type": "Organization",
              name: "TrajetoFácil",
              url: "https://www.trajetofacil.com.br",
            },
          }),
        },
      ],
    };
  },
  notFoundComponent: GuideNotFound,
  component: GuidePage,
});

function GuideNotFound() {
  return (
    <SiteLayout>
      <h1 className="text-2xl font-extrabold tracking-tight text-foreground">Guia não encontrado</h1>
      <p className="mt-3 text-muted-foreground">Este guia não existe ou foi movido.</p>
      <Link to="/guias" className="mt-4 inline-flex items-center gap-1 font-medium text-primary">
        <ArrowLeft className="size-4" /> Ver todos os guias
      </Link>
    </SiteLayout>
  );
}

function GuidePage() {
  const { guide } = Route.useLoaderData();

  return (
    <SiteLayout>
      <nav className="mb-6 text-sm text-muted-foreground">
        <Link to="/guias" className="transition hover:text-foreground">Guias</Link>
        <span className="mx-2">/</span>
        <span className="text-foreground">{guide.title}</span>
      </nav>

      <article className="space-y-8 text-base leading-7 text-foreground">
        <header>
          <h1 className="text-3xl font-extrabold tracking-tight">{guide.title}</h1>
          <p className="mt-3 text-lg leading-8 text-muted-foreground">{guide.intro}</p>
        </header>

        {guide.sections.map((section, i) => (
          <div key={section.heading}>
            {i === 2 ? <AdSlot className="my-2 min-h-28" minHeight={112} /> : null}
            <section className="space-y-3">
              <h2 className="text-xl font-bold">{section.heading}</h2>
              {section.paragraphs.map((p) => (
                <p key={p.slice(0, 40)}>{p}</p>
              ))}
              {section.list ? (
                <ul className="list-disc space-y-1.5 pl-6">
                  {section.list.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              ) : null}
            </section>
          </div>
        ))}

        <aside className="rounded-2xl border border-primary/30 bg-primary/5 p-5">
          <h2 className="text-lg font-bold">Calcule sua rota agora</h2>
          <p className="mt-2 text-sm leading-6 text-muted-foreground">
            Aplique o que você leu: informe origem, destino e os dados do veículo na calculadora gratuita
            do TrajetoFácil e receba distância, tempo, combustível e pedágio — ida e volta.
          </p>
          <Link
            to="/"
            className="mt-4 inline-flex items-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-bold text-primary-foreground transition hover:opacity-90"
          >
            Abrir a calculadora <ArrowRight className="size-4" />
          </Link>
        </aside>
      </article>
    </SiteLayout>
  );
}
